import { Link } from "react-router-dom";
import { ArrowRight, CheckCircle2, AlertTriangle, XCircle } from "lucide-react";
import SEO from "../components/SEO";

const CONTROLLI = [
  { n: "01", nome: "Salto di marginalità", formula: "EBITDA% anno − EBITDA% media 3 anni > 3 punti", cosa: "Rileva variazioni improvvise di redditività da spiegare (ristrutturazioni, poste una tantum, cambio mix)." },
  { n: "02", nome: "Crediti che corrono (DSO)", formula: "Crediti/ricavi × 365, flag se +15gg in un anno", cosa: "I clienti pagano più tardi o ci sono fatture contestate? Segnale di deterioramento commerciale." },
  { n: "03", nome: "Magazzino gonfio (DIO)", formula: "Su costo del venduto, flag +20% senza crescita ricavi", cosa: "Scorte strategiche o invenduto? Il magazzino è la voce più sopravvalutata dai titolari." },
  { n: "04", nome: "Altri ricavi anomali", formula: "(A5 + straordinari) > 5% ricavi", cosa: "Poste non ricorrenti che gonfiano l'EBITDA apparente. Vanno normalizzate." },
  { n: "05", nome: "Capitalizzazioni interne", formula: "Lavori interni > 2% ricavi", cosa: "Costi girati sullo stato patrimoniale invece che a conto economico. Rettifica automatica." },
  { n: "06", nome: "Servizi in salto", formula: "Variazione B7 > 20% non spiegata", cosa: "Consulenze o costi straordinari che alterano il confronto pluriennale." },
  { n: "07", nome: "Tasso implicito fuori scala", formula: "Oneri finanziari / PFN media fuori 2-9%", cosa: "Possibili debiti non visti a bilancio o garanzie prestate. Verifica umana." },
  { n: "08", nome: "Conversione di cassa debole", formula: "FCF/EBITDA medio 3 anni < 40%", cosa: "L'EBITDA c'è ma la cassa no: dove finisce? Circolante, investimenti, prelievi?" },
  { n: "09", nome: "TFR incoerente", formula: "Accantonamento vs costo personale fuori 6,5-7,5%", cosa: "Possibili fuori busta o part-time non dichiarati. Rischio giuslavoristico." },
  { n: "10", nome: "Compenso amministratore", formula: "Da nota integrativa vs banda di mercato", cosa: "Rettifica 1 automatica del Manuale v2.0 senza domande al titolare." },
  { n: "11", nome: "Canoni vs immobili", formula: "B8 basso + nessun immobile in SP", cosa: "Sede di terzi (probabile socio): l'azienda paga canoni di mercato o è comodo?" },
  { n: "12", nome: "Concentrazione incassi (banca)", formula: "Da open banking: primo pagatore > 35%", cosa: "Rileva la vera concentrazione clienti dai flussi reali, non dalle dichiarazioni." },
  { n: "13", nome: "Tensione di cassa (banca)", formula: "Sconfini/insoluti, saldo vs picchi F24", cosa: "Segnale precoce di stress finanziario. Alert al titolare, non punitivo." },
  { n: "14", nome: "Stagionalità", formula: "Coefficiente variazione incassi mensili > 0,35", cosa: "Il piano di pagamento vendor loan userà rate modellate sulla stagione, non fisse." },
  { n: "15", nome: "Dipendenza dal titolare", formula: "Deleghe bancarie mono-firmatario + nessun dirigente", cosa: "Il primo fattore di sconto sul multiplo. Sotto stress si prezza sempre." },
];

const ADVANCE_RATES = [
  { asset: "Immobili strumentali", base: "Perizia o valore OMI prudenziale", rate: "50-65%", note: "Ipoteca I° grado. Un abuso edilizio azzera la garanzia bancaria." },
  { asset: "Macchinari e impianti", base: "Valore usato di mercato (mai netto contabile)", rate: "35-50%", note: "Privilegio speciale ex art. 46 TUB o leasing-back. Generici > speciali." },
  { asset: "Crediti commerciali", base: "Solo < 90gg, esclusi contestati e intercompany", rate: "70-80%", note: "Cessione o pegno. Haircut se primo cliente > 30%." },
  { asset: "Magazzino", base: "Materie prime + finiti liquidi", rate: "20-30%", note: "Il più sopravvalutato dai titolari, il più scontato dalle banche." },
  { asset: "Marchi / immateriali", base: "Solo con royalty o contratti dimostrabili", rate: "0-20%", note: "Nel micro-market, quasi sempre zero prudenziale." },
];

export default function Metodo() {
  return (
    <main data-testid="metodo-page">
      <SEO
        title="Il Metodo GIANO — Valutazione, Prontezza, Struttura del Deal"
        description="Come funziona il motore GIANO: 15 controlli automatici su bilancio e open banking, algoritmo Punteggio di Prontezza 0-100, capacità di debito a tre gambe (attivi/flussi/mercato), advance rate bancarie, modello a 5 anni con DSCR e stress test. Il metodo di valutazione PMI italiane spiegato punto per punto."
        path="/metodo"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "TechArticle",
          "headline": "Il metodo GIANO per la valutazione delle PMI italiane",
          "author": {"@id": "https://gianoholding.com/#organization"},
          "publisher": {"@id": "https://gianoholding.com/#organization"},
          "inLanguage": "it-IT",
          "keywords": "valutazione azienda, EBITDA normalizzato, PFN, multipli settore, DSCR, capacità di debito, passaggio generazionale, cessione PMI Italia, Milano, Lombardia, Veneto",
          "mainEntityOfPage": "https://gianoholding.com/metodo"
        }}
      />
      <section style={{ background: "var(--midnight)", color: "var(--bone)" }} className="py-24 md:py-32">
        <div className="container-x">
          <div className="overline" style={{ color: "var(--gold)" }}>Il Metodo GIANO · Manuale operativo v3.0</div>
          <h1 className="serif-display mt-6" style={{ fontSize: "clamp(44px, 6vw, 88px)", lineHeight: 1.05, maxWidth: 1100 }}>
            Facciata semplice.<br />
            <span style={{ color: "var(--gold)", fontStyle: "italic" }}>Motore feroce.</span>
          </h1>
          <p className="mt-10 text-lg md:text-xl" style={{ opacity: 0.85, maxWidth: 820, lineHeight: 1.7 }}>
            Ogni servizio ha due facce. Davanti: tre frasi che un imprenditore di 62 anni capisce al primo colpo. Dietro: la stessa modellistica con cui un analista di leveraged finance smonterebbe l'operazione — riclassificazioni, capacità di debito sugli attivi, coperture, stress. La semplicità del davanti è guadagnata dalla ferocia del dietro. Mai al suo posto.
          </p>
        </div>
      </section>

      <section className="py-20" style={{ background: "var(--bone)" }}>
        <div className="container-x">
          <div className="overline" style={{ color: "var(--terracotta)" }}>Sezione 2 · Il motore di analisi automatica</div>
          <h2 className="serif-display mt-4" style={{ fontSize: "clamp(32px, 4vw, 52px)", maxWidth: 900 }}>
            15 controlli automatici. Le domande al titolare sono l'ultima risorsa.
          </h2>
          <p className="mt-6 text-lg" style={{ maxWidth: 780, opacity: 0.78, lineHeight: 1.7 }}>
            Il sistema deve prima leggere da solo trend e anomalie dai bilanci XBRL e dai flussi bancari (PSD2 open banking), e fare all'utente solo le domande che i dati non sanno spiegare. Meno domande fa, più è bravo. <b>Obiettivo a 12 mesi: sotto 4 domande in media per pratica</b>, partendo da 10.
          </p>
        </div>
      </section>

      <section className="py-8" style={{ background: "var(--muted)" }}>
        <div className="container-x">
          <div className="space-y-4">
            {CONTROLLI.map(c => (
              <div key={c.n} className="card-flat p-6 md:p-7" data-testid={`controllo-${c.n}`}>
                <div className="flex items-start gap-6">
                  <div className="serif-display" style={{ fontSize: 42, color: "var(--terracotta)", lineHeight: 1, minWidth: 60 }}>{c.n}</div>
                  <div className="flex-1">
                    <h3 className="serif-display" style={{ fontSize: 22 }}>{c.nome}</h3>
                    <div className="mt-2 font-mono text-xs" style={{ opacity: 0.6 }}>{c.formula}</div>
                    <p className="mt-3 text-sm" style={{ lineHeight: 1.65 }}>{c.cosa}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 md:py-24" style={{ background: "var(--bone)" }}>
        <div className="container-x">
          <div className="overline" style={{ color: "var(--terracotta)" }}>Sezione 3 · Capacità di debito a tre gambe</div>
          <h2 className="serif-display mt-4" style={{ fontSize: "clamp(32px, 4vw, 52px)", maxWidth: 900 }}>
            Debito massimo = <span style={{ fontStyle: "italic", color: "var(--terracotta)" }}>minimo</span> delle tre gambe. Mai la media.
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-12">
            <div className="card-flat p-8">
              <div className="font-mono text-xs" style={{ color: "var(--terracotta)" }}>GAMBA 1 · ATTIVI</div>
              <h3 className="serif-display mt-3" style={{ fontSize: 24 }}>Asset-based</h3>
              <p className="mt-4 text-sm" style={{ lineHeight: 1.65 }}>Somma dei cespiti × advance rate bancario. Cosa garantisce il debito se l'operazione va male.</p>
              <div className="mt-4 font-mono text-xs bg-black bg-opacity-5 p-3">Σ (Valore realizzo × advance rate)</div>
            </div>
            <div className="card-flat p-8">
              <div className="font-mono text-xs" style={{ color: "var(--terracotta)" }}>GAMBA 2 · FLUSSI</div>
              <h3 className="serif-display mt-3" style={{ fontSize: 24 }}>Cash-flow lending</h3>
              <p className="mt-4 text-sm" style={{ lineHeight: 1.65 }}>Servizio del debito sostenibile con DSCR minimo 1,3. Cosa ripaga il debito nel normale corso.</p>
              <div className="mt-4 font-mono text-xs bg-black bg-opacity-5 p-3">FCD / 1,3 → PV al tasso di mercato</div>
            </div>
            <div className="card-flat p-8">
              <div className="font-mono text-xs" style={{ color: "var(--terracotta)" }}>GAMBA 3 · MERCATO</div>
              <h3 className="serif-display mt-3" style={{ fontSize: 24 }}>Moltiplicatore prudenziale</h3>
              <p className="mt-4 text-sm" style={{ lineHeight: 1.65 }}>Cosa accettano le banche nel micro-market italiano. Tetto di sistema.</p>
              <div className="mt-4 font-mono text-xs bg-black bg-opacity-5 p-3">2,5 – 3,0 × EBITDA normalizzato</div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-20" style={{ background: "var(--card)" }}>
        <div className="container-x">
          <div className="overline" style={{ color: "var(--terracotta)" }}>Advance rate bancarie · Come valuta il collaterale una banca</div>
          <h2 className="serif-display mt-4" style={{ fontSize: "clamp(28px, 3.5vw, 40px)", maxWidth: 900 }}>La tabella prudenziale che ogni operazione deve rispettare</h2>
          <div className="mt-10 overflow-x-auto">
            <table className="w-full" style={{ borderCollapse: "collapse" }}>
              <thead>
                <tr style={{ borderBottom: "2px solid var(--midnight)" }}>
                  <th className="text-left font-mono text-xs py-3">ATTIVO</th>
                  <th className="text-left font-mono text-xs py-3">BASE DI VALORE</th>
                  <th className="text-right font-mono text-xs py-3">ADVANCE RATE</th>
                  <th className="text-left font-mono text-xs py-3">NOTE</th>
                </tr>
              </thead>
              <tbody>
                {ADVANCE_RATES.map(r => (
                  <tr key={r.asset} style={{ borderBottom: "1px solid var(--border)" }}>
                    <td className="py-4 serif-display" style={{ fontSize: 18 }}>{r.asset}</td>
                    <td className="text-sm py-4" style={{ opacity: 0.85, maxWidth: 260 }}>{r.base}</td>
                    <td className="py-4 text-right font-mono"><b>{r.rate}</b></td>
                    <td className="text-xs py-4" style={{ opacity: 0.7, maxWidth: 300 }}>{r.note}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      <section className="py-24" style={{ background: "var(--midnight)", color: "var(--bone)" }}>
        <div className="container-x text-center">
          <div className="overline" style={{ color: "var(--gold)" }}>Punteggio di Prontezza 0-100</div>
          <h2 className="serif-display mt-4" style={{ fontSize: "clamp(32px, 4vw, 52px)", maxWidth: 1000, margin: "0 auto" }}>
            Quanto è pronta la tua azienda a un processo di cessione?
          </h2>
          <p className="mt-8 text-lg" style={{ opacity: 0.85, maxWidth: 720, margin: "32px auto 0", lineHeight: 1.7 }}>
            5 componenti pesati: qualità e trasparenza dei numeri (25), documenti pronti (25), solidità finanziaria (20), indipendenza dal titolare (15), diversificazione clienti (15). Fasce A/B/C/D con significato scritto.
          </p>
          <Link to="/valutazione" className="btn-primary mt-10 inline-flex" style={{ background: "var(--gold)", color: "var(--midnight)", borderColor: "var(--gold)" }}>
            Calcola il tuo punteggio ora <ArrowRight size={16} />
          </Link>
        </div>
      </section>
    </main>
  );
}
